/**
 * /submit-request — the one place to ask the People team for something: contract and policy
 * questions, pay, benefits and leave, and the office. Linkable from Slack and Confluence, and
 * opened from My Inquiries' "New request" button.
 *
 * Behind the proxy's People Tickets pilot gate until cutover: outside it, the page says so rather
 * than showing a form whose tickets would land in a table nobody is working yet.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useLocation } from 'react-router-dom';
import { LoadingSpinner } from '../../../shared/components/LoadingSpinner';
import { fetchPeopleTicketOptions } from '../services/peopleTicketsApi';
import type { SentReceipt } from '../types/peopleTickets';
import { RequestForm } from '../components/peopleTickets/RequestForm';
import { usePeopleTicketsAccess } from '../components/peopleTickets/usePeopleTicketsAccess';
import { Centered, NotOpenYet, Submitted } from '../components/peopleTickets/RequestStates';
import { BorisAvatar } from '../components/BorisAvatar';

/**
 * The question Chat with Boris could not answer, handed over in navigation STATE — never in the
 * URL, where an employee's question about their health or pay would land in history and analytics.
 */
export interface SubmitRequestState {
  fromBoris?: string;
}

export default function SubmitRequestView() {
  const { access, loading } = usePeopleTicketsAccess();
  const handedOver = (useLocation().state as SubmitRequestState | null)?.fromBoris;
  const fromBoris = typeof handedOver === 'string' ? handedOver : '';
  const [sent, setSent] = useState<SentReceipt | null>(null);
  const [formKey, setFormKey] = useState(0);
  const options = useQuery({
    queryKey: ['people-tickets-options'],
    queryFn: fetchPeopleTicketOptions,
    enabled: Boolean(access?.enabled),
    staleTime: 30 * 60_000,
  });

  if (loading || (access?.enabled && options.isLoading)) {
    return (
      <Centered>
        <LoadingSpinner size="lg" aria-label="Loading the request form" />
      </Centered>
    );
  }
  if (!access?.enabled) return <NotOpenYet />;
  if (options.isError || !options.data) {
    return (
      <Centered>
        <p className="text-sm text-red-700">The form could not be loaded. Please reload the page.</p>
      </Centered>
    );
  }

  if (sent) {
    return (
      <Submitted
        receipt={sent}
        onAnother={() => {
          setSent(null);
          setFormKey((k) => k + 1);
        }}
      />
    );
  }

  return (
    <div className="mx-auto max-w-2xl p-6">
      <div className="flex items-center gap-3">
              <BorisAvatar size="lg" />
              <h1 className="text-2xl font-semibold text-black">Tell Boris what you need</h1>
            </div>
      <p className="mb-6 mt-3 body-14-regular text-iron">
        Tell us what you need and it reaches the right person straight away. You can follow up in{' '}
              <Link to="/my-inquiries" className="text-trustedBlue hover:underline">
                My inquiries
              </Link>
              .
      </p>
      {fromBoris && formKey === 0 && (
        <p className="mb-4 body-14-regular text-iron">
          We've added your question from the chat below — pick a category and add anything that helps.
        </p>
      )}
      <RequestForm
        key={formKey}
        options={options.data}
        onSubmitted={setSent}
        // Only the first form: "Submit another" should start empty, not repeat the chat question.
        initialDescription={formKey === 0 ? fromBoris : ''}
      />
    </div>
  );
}
